<template>
  <!-- FT-192 (REQ-09): пара строк «статья дохода | статья расхода» экранной таблицы общего отчёта.
       Корень — <tbody>: в таблице их может быть несколько, и раскрытие подкатегорий добавляет <tr>
       под парой, не ломая вёрстку «доход и расход в одном <tr>» (в одном <tr> две половины,
       а у раскрытых половин подстроки идут парами по индексу). Разделители строк Vuetify рисует
       только у `tbody > tr:not(:last-child)`, поэтому в своём <tbody> их возвращает <style> ниже.
       С `side` (телефон: доходы и расходы двумя таблицами) рисуется одна половина без пустых ячеек
       другой; если у пары нет статьи этой стороны — компонент не рисует ничего (комментарий). -->
  <tbody v-if="visible" class="report-category-rows">
    <tr>
      <template v-for="side in sides">
        <template v-if="side.cfg">
          <th :key="side.key + '-name'" :class="side.headClass" class="report-category-name" style="cursor: pointer"
            :data-testid="'report-category-' + side.cfg.field" @click="open(side.cfg)">
            <v-icon v-if="side.breakdown.length > 0" small role="button" tabindex="0" class="mr-1"
              :aria-expanded="side.expanded ? 'true' : 'false'"
              :aria-label="side.expanded ? $t('analitics.hide_subcategories') : $t('analitics.show_subcategories')"
              :title="side.expanded ? $t('analitics.hide_subcategories') : $t('analitics.show_subcategories')"
              :data-testid="'report-category-toggle-' + side.cfg.field"
              @click.stop="toggle(side.key)" @keydown.enter.stop.prevent="toggle(side.key)"
              @keydown.space.stop.prevent="toggle(side.key)">
              {{ side.expanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
            </v-icon>{{ side.cfg.title }}
          </th>
          <td :key="side.key + '-sum'" class="text-center" style="cursor: pointer" @click="open(side.cfg)">
            {{ money(report[side.cfg.field]) }}
          </td>
          <td :key="side.key + '-part'" class="text-center" style="cursor: pointer" @click="open(side.cfg)">
            {{ part(report[side.cfg.field], report[side.key]) }}
          </td>
        </template>
        <template v-else>
          <th :key="side.key + '-name'" :class="side.headClass"></th>
          <td :key="side.key + '-sum'" class="text-center"></td>
          <td :key="side.key + '-part'" class="text-center"></td>
        </template>
      </template>
    </tr>

    <tr v-for="(pair, index) in expansionRows" :key="'sub-' + index" class="report-subcategory-row">
      <template v-for="side in sides">
        <template v-if="pair[side.key]">
          <td :key="side.key + '-sub-name'" :class="side.subClass" class="report-subcategory-name"
            style="cursor: pointer; padding-left: 28px"
            :data-testid="'report-subcategory-' + side.cfg.field + '-' + subKey(pair[side.key])"
            @click="openSub(side.cfg, pair[side.key])">
            <v-icon v-if="pair[side.key].id !== null && pair[side.key].active === false" x-small class="mr-1"
              :title="$t('analitics.subcategory_hidden')">mdi-eye-off-outline</v-icon>{{ subTitle(pair[side.key]) }}
          </td>
          <td :key="side.key + '-sub-sum'" class="text-center" style="cursor: pointer"
            @click="openSub(side.cfg, pair[side.key])">
            {{ money(pair[side.key].sum) }}
          </td>
          <td :key="side.key + '-sub-part'" class="text-center" style="cursor: pointer"
            @click="openSub(side.cfg, pair[side.key])">
            {{ part(pair[side.key].sum, report[side.cfg.field]) }}
          </td>
        </template>
        <td v-else :key="side.key + '-sub-empty'" colspan="3"></td>
      </template>
    </tr>
  </tbody>
</template>

<script>
import { NO_SUBCATEGORY } from "@/utils/countCategory";

export default {
  name: "ReportCategoryRow",
  props: {
    report: { type: Object, required: true },
    currencySymbol: { type: String, default: "" },
    // { field, title } — поле отчёта и подпись статьи; null — половина пары пуста
    income: { type: Object, default: null },
    expense: { type: Object, default: null },
    // "income" | "expense" — рисовать только эту половину пары; null — обе, как в парной таблице
    side: { type: String, default: null, validator: (value) => value === null || value === "income" || value === "expense" },
  },
  data() {
    return { expanded: { income: false, expense: false } };
  },
  computed: {
    dark() {
      return !!(this.$vuetify && this.$vuetify.theme && this.$vuetify.theme.dark);
    },
    // Две половины пары: ключ совпадает с полем итога отчёта (income / expense);
    // breakdown — подкатегории половины, считаются здесь один раз.
    sides() {
      const sides = [
        {
          key: "income",
          cfg: this.income,
          breakdown: this.breakdownOf(this.income),
          expanded: this.expanded.income,
          headClass: this.dark ? "green darken-1" : "green lighten-4",
          subClass: this.dark ? "green darken-3" : "green lighten-5",
        },
        {
          key: "expense",
          cfg: this.expense,
          breakdown: this.breakdownOf(this.expense),
          expanded: this.expanded.expense,
          headClass: this.dark ? "red darken-1" : "red lighten-4",
          subClass: this.dark ? "red darken-3" : "red lighten-5",
        },
      ];
      return this.side ? sides.filter((side) => side.key === this.side) : sides;
    },
    // Без `side` рисуется всегда; с `side` — только если у пары есть статья этой стороны.
    visible() {
      return !this.side || this.sides.some((side) => side.cfg);
    },
    // Подстроки раскрытых половин, склеенные по индексу: i-я строка таблицы — i-я подкатегория
    // левой и правой половины (короче половина — добивается пустой ячейкой на три колонки).
    // С `side` половина одна, пустых ячеек нет.
    expansionRows() {
      const columns = this.sides.map((side) => (side.expanded ? side.breakdown : []));
      const count = Math.max(0, ...columns.map((list) => list.length));
      const rows = [];
      for (let i = 0; i < count; i += 1) {
        const pair = { income: null, expense: null };
        this.sides.forEach((side, index) => {
          pair[side.key] = columns[index][i] || null;
        });
        rows.push(pair);
      }
      return rows;
    },
  },
  methods: {
    // Старый отчёт: subcategory_breakdown === null, поля без подкатегорий отсутствуют.
    breakdownOf(cfg) {
      if (!cfg) return [];
      const breakdown = this.report && this.report.subcategory_breakdown;
      const list = breakdown && breakdown[cfg.field];
      return Array.isArray(list) ? list : [];
    },
    toggle(key) {
      this.expanded = { ...this.expanded, [key]: !this.expanded[key] };
    },
    // Те же числа, что у ручных строк: одна цифра после запятой, знак валюты за суммой.
    money(value) {
      return `${parseFloat(value).toFixed(1)}${this.currencySymbol || ""}`;
    },
    part(value, total) {
      const divisor = parseFloat(total) === 0 || total == null ? 1 : total;
      return `${parseFloat((value / divisor) * 100).toFixed(1)}%`;
    },
    // id подкатегории для селектора и запроса; «Без подкатегории» — NO_SUBCATEGORY
    subKey(sub) {
      return sub.id === null ? NO_SUBCATEGORY : sub.id;
    },
    subTitle(sub) {
      return sub.id === null ? this.$t("counts.no_subcategory") : sub.value;
    },
    open(cfg) {
      this.$emit("details", cfg.field, cfg.title);
    },
    // «Статья · Подкатегория»; «Без подкатегории» уходит на сервер как NO_SUBCATEGORY (none).
    openSub(cfg, sub) {
      this.$emit("details", cfg.field, `${cfg.title} · ${this.subTitle(sub)}`, this.subKey(sub));
    },
  },
};
</script>

<style>
/* Разделители строк, как в v-simple-table (VSimpleTable.sass): граница под каждой строкой,
   кроме последней в <tbody>. Пара живёт в своём <tbody> — у единственной строки свёрнутой статьи
   и у последней подстроки раскрытой Vuetify границу не рисует, а за ними идёт следующая пара
   или итоги. Цвет — `dividers` темы Vuetify 2 (светлая rgba(0,0,0,.12), тёмная rgba(255,255,255,.12)). */
.theme--light.v-data-table .report-category-rows > tr > td,
.theme--light.v-data-table .report-category-rows > tr > th {
  border-bottom: thin solid rgba(0, 0, 0, 0.12);
}
.theme--dark.v-data-table .report-category-rows > tr > td,
.theme--dark.v-data-table .report-category-rows > tr > th {
  border-bottom: thin solid rgba(255, 255, 255, 0.12);
}
</style>
